import styled, { css } from 'styled-components';
import { Breakpoints } from '../../../../styled/constants';
import {
  FlexibleDiv,
  ellipsisText,
  FlexBetween
} from '../../../../styled/common';
import { Typography } from '../../../ui/Typography';

const filtersSmallSize = '44px';
const filtersBigSize = '54px';

export const InitialSearchBarContainer = styled(FlexibleDiv)<{
  $isSearchPage: boolean;
  $isHomePage: boolean;
}>`
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 8px;

  @media screen and (min-width: ${Breakpoints.desktop}px) {
    max-width: 1280px;
    margin: 0 auto;
  }

  ${({ $isSearchPage }) =>
    $isSearchPage &&
    css`
      padding: 15px;
      margin-bottom: 15px;
      background-color: ${({ theme }) => theme.colors.background.tertiary};
      border-radius: 21px 21px 0 0;

      @media screen and (min-width: ${Breakpoints.tablet}px) {
        padding: 0 0 35px;
        margin-bottom: 0;
        background-color: ${({ theme }) => theme.colors.background.primary};
        border-radius: 0;
      }

      @media screen and (min-width: ${Breakpoints.desktop}px) {
        padding-bottom: 30px;
      }
    `}
`;

export const FiltersContainer = styled(FlexibleDiv)<{
  $isShowHomeFilters: boolean;
}>`
  width: 100%;
  flex-wrap: ${({ $isShowHomeFilters }) =>
    $isShowHomeFilters ? 'nowrap' : 'wrap'};
  gap: 8px;

  @media (min-width: ${Breakpoints.tablet}px) {
    flex-wrap: nowrap;
  }
`;

export const FilterWrapper = styled.div<{
  $isShowHomeFilters: boolean;
  $hideOnMedium?: boolean;
}>`
  flex: 1 1 calc(50% - 4px);
  width: calc(50% - 4px);

  @media (min-width: ${Breakpoints.tablet}px) {
    flex: 0 0 auto;
    min-width: 80px;
    max-width: 14%;

    &:first-child {
      max-width: 101px;
    }

    &:nth-child(2) {
      max-width: 120px;
    }
  }

  @media (min-width: ${Breakpoints.smallDesktop}px) {
    &:first-child,
    &:nth-child(2) {
      max-width: 164px;
    }
  }

  ${({ $isShowHomeFilters }) =>
    $isShowHomeFilters &&
    css`
      display: none;

      @media (min-width: ${Breakpoints.tablet}px) {
        display: block;
      }
    `}

  ${({ $hideOnMedium }) =>
    $hideOnMedium &&
    css`
      @media (min-width: ${Breakpoints.tablet}px) and (max-width: ${Breakpoints.desktop -
        1}px) {
        display: none;
      }
    `}
`;

export const SelectButton = styled.button<{ $isShowHomeFilters: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px;
  background-color: ${({ theme }) => theme.colors.background.primary};
  gap: 5px;
  height: ${({ $isShowHomeFilters }) =>
    $isShowHomeFilters ? filtersBigSize : filtersSmallSize};
  width: 100%;
  justify-content: space-between;
  box-sizing: border-box;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 14px;

  @media (min-width: ${Breakpoints.tablet}px) {
    height: ${filtersBigSize};
  }

  svg path {
    fill: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const TextsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  width: 78%;
`;

export const SelectText = styled(Typography).attrs({ tag: 'body_3' })<{
  $color?: string;
}>`
  color: ${({ theme, $color = 'secondary' }) =>
    theme.colors.text[$color as keyof typeof theme.colors.text]};
  text-align: left;
  ${ellipsisText};
`;

export const ArrowIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(90deg);
`;

export const LocationBarWrapper = styled.div<{ $isShowHomeFilters: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 14px;
  overflow: hidden;
  position: relative;
  width: calc(
    100% -
      ${({ $isShowHomeFilters }) =>
        $isShowHomeFilters ? filtersBigSize : filtersSmallSize} -
      10px
  );
  height: ${({ $isShowHomeFilters }) =>
    $isShowHomeFilters ? filtersBigSize : filtersSmallSize};

  @media (min-width: ${Breakpoints.tablet}px) {
    width: 100%;
    height: ${filtersBigSize};
  }
`;

export const LocationBar = styled(FlexBetween)`
  position: relative;
  border-radius: 14px;
  padding: 0 10px;
  height: 100%;
  cursor: pointer;
  flex: 1;
  min-width: 0;
  background-color: ${({ theme }) => theme.colors.background.primary};
`;

export const LocationBarText = styled(FlexBetween)`
  width: 100%;
  height: 100%;
`;

export const LocationPlaceholder = styled.div`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.text.primary};
  ${ellipsisText};
`;

export const LocationCity = styled(Typography)`
  color: ${({ theme }) => theme.colors.systemColors.blue};
  padding: 18px 0;
  padding-left: 10px;
  cursor: pointer;
  white-space: nowrap;
`;

export const FilterButton = styled.button<{ $isShowHomeFilters: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.border.secondary};
  border-radius: 14px;
  background-color: ${({ theme }) => theme.colors.background.primary};
  width: ${({ $isShowHomeFilters }) =>
    $isShowHomeFilters ? filtersBigSize : filtersSmallSize};
  height: ${({ $isShowHomeFilters }) =>
    $isShowHomeFilters ? filtersBigSize : filtersSmallSize};

  @media (min-width: ${Breakpoints.tablet}px) {
    width: ${filtersBigSize};
    height: ${filtersBigSize};
    padding: 17px;
  }

  @media (min-width: ${Breakpoints.desktop}px) {
    width: 128px;
    padding: 15px;
    gap: 5px;
  }

  svg path {
    fill: ${({ theme }) => theme.colors.text.primary};
    stroke: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const FilterButtonText = styled(Typography)`
  display: none;

  @media (min-width: ${Breakpoints.desktop}px) {
    display: block;
  }
`;

export const SearchButton = styled.button`
  display: none;

  @media (min-width: ${Breakpoints.smallDesktop}px) {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 74px;
    height: ${filtersBigSize};
    flex-shrink: 0;
    border-radius: 14px;
    border: none;
    background-color: ${({ theme }) => theme.colors.systemColors.blue};
    cursor: pointer;
  }
`;

export const SearchButtonText = styled(Typography)`
  color: ${({ theme }) => theme.colors.background.primary};
`;
